import {
  IAttributeField,
  IInsightDialog,
  IInsightPoints,
  IInsightTip,
  IInsightWidget,
  OfferSubType,
  OfferType,
  Severity,
  SeverityState
} from '@jumpstart/core';
import { IInsightReminder, WidgetType } from '@jumpstart/core/dist/schemas/insight/insight';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { headerCase } from 'change-case';
import { FieldArray, Form, FormikProps } from 'formik';
import { nanoid } from 'nanoid';
import React, { ChangeEvent, useState } from 'react';
import { formStyles } from '../styles';
import ContentBaseField from './ContentBaseField';
import ContentField from './ContentField';
import DialogFormActionsField from './DialogFormActionsField';
import { CTEXT } from './DialogFormConfig';
import DialogFormResponseField from './DialogFormResponseField';
import { isCompletion, IValidation } from './utils';
import WidgetField from './WidgetField';


export interface IDialogForm extends FormikProps<IInsightDialog & { answered: boolean }> {
  answer: string | null;
  validation: IValidation;
}

export default function DialogForm(props: IDialogForm) {
  const { errors, values, handleChange, handleSubmit, setFieldValue, answer, validation, touched } = props;
  const [newAttribute, setNewAttribute] = useState('');
  const [newAttributePath, setNewAttributePath] = useState<null | string>(null);

  const defaultAttributeField = (): IAttributeField => {
    return {
      value: 1,
      min: 0,
      max: 1
    };
  };

  const numPoints = (points?: IInsightPoints): number => {
    let p: number = 0;
    if (points) {
      Object.keys(points).forEach(l => {
        if (points.hasOwnProperty(l) && typeof (points as any)[l] === 'number') {
          p++;
        }
      });
    }
    return p;
  };


  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <Grid container direction="column" sx={formStyles.root}>
        <div>
          {
            Object.keys(errors).length > 0 &&
            <Alert severity="error">
              <ul>{Object.keys(errors).map((key: string) => <li key={key}>{(errors as any)[key]}</li>)}</ul>
            </Alert>
          }
          <Accordion
            sx={formStyles.input}
            defaultExpanded={true}
            expanded={true}
          >
            <AccordionSummary>
              <Typography>Dialog Fields</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container direction="column" sx={formStyles.section}>
                <ContentBaseField
                  id="id"
                  name="id"
                  value={values.id && (`${values.id}`).length > 3 ? values.id : nanoid(10)}
                  variant="outlined"
                  label="ID"
                  type="text"
                  placeholder="Autogenerated after save"
                  disabled
                  validation={validation}
                  touched={!!touched.id}
                  error={!!errors.id}
                  helperText={errors.id}
                  InputLabelProps={{ shrink: true }}
                  InputProps={
                    {
                      notched: true,
                      endAdornment:
                        <IconButton onClick={() => {
                          setFieldValue('id', nanoid(10));
                        }}><RefreshIcon/></IconButton>
                    }
                  }
                />

                <ContentBaseField
                  id="notes"
                  name="notes"
                  defaultValue={(values as any).notes}
                  onBlur={handleChange}
                  variant="outlined"
                  label="📝 Notes"
                  helperText="Internal debugging notes"
                  placeholder="Debug notes"
                  type="text"
                  validation={validation}
                  touched={!!touched.id}
                  InputLabelProps={{ shrink: true }}
                  multiline
                />

                {answer !== 'N/A' &&
                <ContentField
                  id={`response`}
                  name={`response`}
                  defaultValue={values.response}
                  onBlur={handleChange}
                  sx={formStyles.input}
                  style={{ width: '100%' }}
                  variant="outlined"
                  label={`${isCompletion(values.completionCriteria) ? '⭐' : '💬'} Response to "${answer}"`}
                  placeholder="Response"
                  error={!!errors.response}
                  helperText={!errors.response ? `Immediate response we provide to "${answer}"` : errors.response}
                  multiline
                  InputLabelProps={{ shrink: true }}
                  validation={validation}
                  touched={!!touched.response}
                />
                }

                {isCompletion(values.completionCriteria) &&
                <Typography variant="body2">Marked as a completion event</Typography>}

                <Collapse in={!isCompletion(values.completionCriteria)}>
                  <Grid item container direction="row" alignItems="center" style={{ marginBottom: 12 }} wrap="nowrap">

                    <Grid item container>
                      <ContentField
                        id={`body`}
                        name={`body`}
                        defaultValue={values.body}
                        onBlur={handleChange}
                        sx={formStyles.input}
                        style={{ width: '100%' }}
                        variant="outlined"
                        label={`✔ ${values.isAction ? 'Action' : 'Context'}`}
                        placeholder={values.isAction ? 'Action' : 'Context'}
                        error={!!errors.body}
                        helperText={!errors.body ? `What do you want the user to do? ${CTEXT(
                          values.body,
                          'body'
                        )}` : `${errors.body} ${CTEXT(values.body, 'body')}`}
                        validation={validation}
                        touched={!!touched.body}
                        multiline
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            key={`isAction`}
                            id={`isAction`}
                            name={`isAction`}
                            checked={values.isAction}
                            onChange={
                              (event: ChangeEvent<HTMLInputElement>) => setFieldValue(
                                'isAction',
                                event.target.checked
                              )
                            }
                          />
                        }
                        style={{ color: '#000', textAlign: 'center', fontSize: 10 }}
                        label={<Typography variant="caption">{values.isAction ? 'Action' : 'Context'}</Typography>}
                        labelPlacement="bottom"
                      />
                    </Grid>
                  </Grid>

                  <Grid container direction="column" style={{}}>
                    <Grid item container direction="row" alignItems="center" style={{ marginBottom: 12 }} wrap="nowrap">
                      <Grid item container>
                        <ContentField
                          id={`question`}
                          name={`question`}
                          defaultValue={values.question}
                          onBlur={handleChange}
                          sx={formStyles.input}
                          variant="outlined"
                          label={`❓ Question`}
                          error={!!errors.question}
                          helperText={!errors.question ? `What we want the user to answer. ${CTEXT(
                            values.question,
                            'question'
                          )}` : errors.question}
                          validation={validation}
                          touched={!!touched.question}
                          multiline
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              key={`questionInline`}
                              id={`questionInline`}
                              name={`questionInline`}
                              checked={values.questionInline}
                              onChange={
                                (event: ChangeEvent<HTMLInputElement>) => {
                                  setFieldValue('questionInline', event.target.checked);
                                  if (event.target.checked) {
                                    setFieldValue('isAction', false);
                                  }
                                }
                              }
                            />
                          }
                          style={{ color: '#000', textAlign: 'center', fontSize: 10 }}
                          label={<Typography
                            variant="caption">{values.questionInline ? 'Inline' : 'Bubbled'}</Typography>}
                          labelPlacement="bottom"
                        />
                      </Grid>
                    </Grid>

                    <Grid container direction="column" style={{ paddingLeft: '24px', marginBottom: 18 }}>
                      <DialogFormResponseField values={values} classes={formStyles} setFieldValue={setFieldValue}/>
                    </Grid>
                  </Grid>

                </Collapse>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={formStyles.input}>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
              <Grid container direction="column">
                <Typography variant="body1">📝 Additional Content</Typography>
                <Typography variant="caption" color="textSecondary">Content control variables</Typography>
              </Grid>
            </AccordionSummary>

            <Grid container direction="column" wrap="nowrap">
              <ContentField
                id={`header`}
                name={`header`}
                defaultValue={values.header}
                onBlur={handleChange}
                sx={formStyles.input}
                style={{ width: '100%' }}
                variant="outlined"
                label={`Action Header`}
                placeholder="Action Header (Optional)"
                helperText="Optional header for the action"
                validation={validation}
                touched={!!touched.header}
                multiline
              />

              <TextField
                id={`mediaId`}
                name={`mediaId`}
                defaultValue={values.mediaId}
                onBlur={handleChange}
                sx={formStyles.input}
                style={{ width: '100%' }}
                variant="outlined"
                label={`Media`}
                helperText="GraphCMS Asset to use"
                multiline
              />

              <ContentField
                id={`questionOtherLabel`}
                name={`questionOtherLabel`}
                defaultValue={values.questionOtherLabel}
                onBlur={handleChange}
                sx={formStyles.input}
                variant="outlined"
                label={`Exit Option Label`}
                placeholder="Something Else"
                error={!!errors.questionOtherLabel}
                helperText={!errors.questionOtherLabel ? null : errors.questionOtherLabel}
                validation={validation}
                InputLabelProps={{ shrink: true }}
                touched={!!touched.questionOtherLabel}
                multiline
              />
              {!values.questionInline && <Grid item>
                <ContentField
                  id={`questionShort`}
                  name={`questionShort`}
                  defaultValue={values.questionShort ? values.questionShort : 'Did you succeed?'}
                  onBlur={handleChange}
                  sx={formStyles.input}
                  variant="outlined"
                  label={`❓ Question (Short)`}
                  error={!!errors.questionShort}
                  helperText={!errors.questionShort ? `Question on the button, defaults to "Did you succeed?"ww ${CTEXT(
                    values.questionShort,
                    'questionShort'
                  )}` : errors.questionShort}
                  validation={validation}
                  touched={!!touched.questionShort}
                  multiline
                />
              </Grid>}
            </Grid>
          </Accordion>

          <Accordion sx={formStyles.input}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Grid container direction="column">

                <Typography variant="body1">⏱️Reminders
                  ({(values.reminders ? values.reminders : []).length})</Typography>
                <Typography variant="caption" color="textSecondary">Determines when push notifications
                  happen</Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <FieldArray
                name={`reminders`}>
                {(arrayHelpers) =>
                  <Grid container direction="column" wrap="nowrap">
                    {(values.reminders ? values.reminders : []).map((reminder: IInsightReminder, index: number) =>
                      <Grid container key={`reminders[${index}]`} direction="row">
                        <Grid item xs={6}>
                          <TextField
                            key={`reminders[${index}].day`}
                            id={`reminders[${index}].day`}
                            name={`reminders[${index}].day`}
                            defaultValue={reminder.day}
                            onBlur={handleChange}
                            sx={formStyles.input}
                            style={{ width: '100%' }}
                            label="Day"
                            variant="outlined"
                            type="number"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <ContentField
                            key={`reminders[${index}].message`}
                            id={`reminders[${index}].message`}
                            name={`reminders[${index}].message`}
                            defaultValue={reminder.message}
                            onBlur={handleChange}
                            sx={formStyles.input}
                            style={{ width: '100%' }}
                            variant="outlined"
                            label="Message"
                            helperText="What text do you want appended to the message. It will be appended before the question."
                            validation={validation}
                            touched={!!touched.reminders}
                            multiline
                            // InputLabelProps={{shrink: true}}
                            InputProps={
                              {
                                notched: true,
                                additionalEndAdornments: <IconButton
                                  size="small"
                                  onClick={() => arrayHelpers.remove(index)}
                                >
                                  <CloseIcon/>
                                </IconButton>
                              }
                            }
                          />
                        </Grid>
                      </Grid>
                    )}
                    <Button startIcon={<AddIcon/>} onClick={() => arrayHelpers.push({ day: 0, message: '' })}>Add
                      Reminder</Button>
                  </Grid>
                }
              </FieldArray>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={formStyles.input}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Grid container direction="column">

                <Typography variant="body1">☑️Instructions
                  ({(values.highlights ? values.highlights : []).length})</Typography>
                <Typography variant="caption" color={errors.highlights ? 'error' : 'textSecondary'}>
                  {errors.highlights ? errors.highlights : 'What to highlight for the user to do (use this as direction steps)'}
                </Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <FieldArray
                name={`highlights`}>
                {(arrayHelpers) =>
                  <Grid container direction="column" wrap="nowrap">
                    {(values.highlights ? values.highlights : []).map((highlight: string, index: number) =>
                      <ContentField
                        key={`highlights[${index}]`}
                        id={`highlights[${index}]`}
                        name={`highlights[${index}]`}
                        defaultValue={highlight}
                        onBlur={handleChange}
                        sx={formStyles.input}
                        style={{ width: '100%' }}
                        variant="outlined"
                        validation={validation}
                        touched={!!touched.highlights}
                        multiline
                        helperText={`${values.highlights && CTEXT(values.highlights[index], 'highlights')}`}
                        // InputLabelProps={{shrink: true}}
                        InputProps={
                          {
                            notched: true,
                            additionalEndAdornments: <IconButton
                              size="small"
                              onClick={() => arrayHelpers.remove(index)}
                            >
                              <CloseIcon/>
                            </IconButton>
                          }
                        }
                      />
                    )}
                    <Button startIcon={<AddIcon/>} onClick={() => arrayHelpers.push('')}>Add Highlight</Button>
                  </Grid>
                }
              </FieldArray>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={formStyles.input}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Grid container direction="column">
                <Typography variant="body1">🐕 Attributes
                  ({(values.metaAttributes ? values.metaAttributes : []).length})</Typography>
                <Typography variant="caption" color="textSecondary">Snapshot knowledge of what we learned if the
                  user reaches here</Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container direction="column" wrap="nowrap">
                {Object.keys((values.metaAttributes ? values.metaAttributes : {}))
                  .map((attribute: string, index: number) =>
                    <Grid key={`metaAttributes.${attribute}`} item container direction="row" wrap="nowrap">
                      <Grid item xs={4}>
                        <TextField
                          key={`metaAttributes.${attribute}.value`}
                          id={`metaAttributes.${attribute}.value`}
                          name={`metaAttributes.${attribute}.value`}
                          label={`Value`}
                          defaultValue={values.metaAttributes && values.metaAttributes.hasOwnProperty(attribute) && values.metaAttributes[attribute].value ? values.metaAttributes[attribute].value : null}
                          onBlur={handleChange}
                          sx={formStyles.input}
                          style={{ width: '100%' }}
                          variant="outlined"
                          type="number"
                          helperText="Check to slap a range"
                          InputProps={
                            {
                              notched: true,
                              endAdornment: <React.Fragment>
                                <Checkbox
                                  key={`metaAttributes.${attribute}.hasRange`}
                                  id={`metaAttributes.${attribute}.hasRange`}
                                  name={`metaAttributes.${attribute}.hasRange`}
                                  onChange={
                                    (event: ChangeEvent<HTMLInputElement>) => setFieldValue(
                                      `metaAttributes.${attribute}.hasRange`,
                                      event.target.checked
                                    )
                                  }
                                />
                                <IconButton
                                  size="small"
                                  edge="end"
                                  aria-label="delete"
                                  style={{ marginLeft: 12 }}
                                  onClick={() => {
                                    const { [attribute]: x, ...remaining } = values.metaAttributes as any;
                                    console.log(attribute, remaining);
                                    setFieldValue(`metaAttributes`, remaining);
                                  }}
                                >
                                  <DeleteIcon/>
                                </IconButton>
                              </React.Fragment>
                            }
                          }
                        />
                      </Grid>
                      {values.metaAttributes && values.metaAttributes.hasOwnProperty(attribute) && values.metaAttributes[attribute].value ?
                        <React.Fragment>
                          <Grid item xs={4}>
                            <TextField
                              key={`metaAttributes.${attribute}.min`}
                              id={`metaAttributes.${attribute}.min`}
                              name={`metaAttributes.${attribute}.min`}
                              label={`Min`}
                              defaultValue={values.metaAttributes && values.metaAttributes.hasOwnProperty(attribute) && typeof values.metaAttributes[attribute].min === 'number' ? values.metaAttributes[attribute].min : 0}
                              onBlur={handleChange}
                              sx={formStyles.input}
                              style={{ width: '100%' }}
                              variant="outlined"
                              type="number"
                              error={values.metaAttributes && values.metaAttributes.hasOwnProperty(attribute) && typeof values.metaAttributes[attribute].min === 'number' && typeof values.metaAttributes[attribute].max === 'number' && (values.metaAttributes as any)[attribute].min > (values.metaAttributes as any)[attribute].max}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              key={`metaAttributes.${attribute}.max`}
                              id={`metaAttributes.${attribute}.max`}
                              name={`metaAttributes.${attribute}.max`}
                              label={`Max`}
                              defaultValue={values.metaAttributes && values.metaAttributes.hasOwnProperty(attribute) && typeof values.metaAttributes[attribute].max === 'number' ? values.metaAttributes[attribute].max : 1}
                              onBlur={handleChange}
                              sx={formStyles.input}
                              style={{ width: '100%' }}
                              variant="outlined"
                              type="number"
                            />
                          </Grid>
                        </React.Fragment>
                        : null
                      }
                    </Grid>
                  )}
                <Collapse in={!newAttributePath} sx={formStyles.input}>
                  <Button
                    startIcon={<AddIcon/>}
                    onClick={() => {
                      setNewAttributePath(`metaAttributes`);
                      setTimeout(() => {
                        const el = document.getElementById(`metaAttributes.new-response-ref`);
                        if (el) {
                          el.scrollIntoView();
                        }
                      }, 1000);
                    }}
                  >
                    Add Attribute
                  </Button>
                </Collapse>
                <Collapse in={!!newAttributePath && newAttributePath === `metaAttributes`}
                          sx={formStyles.input}>
                  <Grid container direction="row" sx={formStyles.input}>
                    <TextField
                      id={`metaAttributes.new-response-ref`}
                      value={newAttribute}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewAttributePath(e.target.value)}
                      sx={formStyles.input}
                      variant="outlined"
                      label="Response"
                      InputProps={
                        {
                          notched: true,
                          endAdornment: <React.Fragment>
                            <IconButton onClick={() => {
                              setNewAttributePath(null);
                              setNewAttribute('');
                              setFieldValue(`metaAttributes.${newAttribute}`, defaultAttributeField());
                            }}><CheckIcon/></IconButton>
                            <IconButton onClick={() => {
                              setNewAttributePath(null);
                              setNewAttribute('');
                            }}><CloseIcon/></IconButton>
                          </React.Fragment>
                        }
                      }
                    />
                  </Grid>
                </Collapse>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={formStyles.input}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Grid container direction="column">
                <Typography variant="body1">💎 Widgets
                  ({(values.widgets ? values.widgets : []).length})</Typography>
                <Typography variant="caption" color="textSecondary">Additional widgets to display to the
                  user (products, services, insights)</Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <FieldArray
                name={`widgets`}>
                {(arrayHelpers) =>
                  <Grid container direction="column" wrap="nowrap">
                    {(values.widgets ? values.widgets : []).map((widget: IInsightWidget, index: number) =>
                      <WidgetField values={values} widget={widget} index={index} classes={formStyles}
                                   arrayHelpers={arrayHelpers} handleChange={handleChange}
                                   setFieldValue={setFieldValue}
                                   touched={touched}
                                   validation={validation}
                      />
                    )}
                    <Button
                      startIcon={<AddIcon/>}
                      onClick={() => {
                        const defaultWidget: IInsightWidget = {
                          widget: WidgetType.PRODUCT,
                          requirements: [],
                          purpose: '',
                          title: '',
                          meta: {
                            type: OfferType.PRODUCT,
                            subtype: OfferSubType.UNKNOWN,
                            severity: Severity.MINOR
                          }
                        };
                        arrayHelpers.push(defaultWidget);
                      }}
                    >
                      Add Widget
                    </Button>
                  </Grid>
                }
              </FieldArray>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={formStyles.input}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Grid container direction="column">
                <Typography variant="body1">❕ Tips ({(values.tips ? values.tips : []).length})</Typography>
                <Typography variant="caption"
                            color={errors.tips ? 'error' : 'textSecondary'}>{errors.tips ? errors.tips : 'Helpful tips to display to the user'}</Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <FieldArray
                name={`tips`}
              >
                {(tipArrayHelpers) =>
                  <Grid container direction="column" wrap="nowrap">
                    {(values.tips ? values.tips : []).map((tip: IInsightTip, tipIndex: number) =>
                      <Grid key={`tips[${tipIndex}]`} container direction="row" wrap="nowrap"
                            sx={formStyles.input}>

                        <ContentField
                          key={`tips[${tipIndex}].message`}
                          id={`tips[${tipIndex}].message`}
                          name={`tips[${tipIndex}].message`}
                          label={`Tip ${tipIndex}`}
                          defaultValue={tip.message}
                          onBlur={handleChange}
                          sx={formStyles.input}
                          variant="outlined"
                          placeholder="Tip"
                          multiline
                          validation={validation}
                          touched={!!touched.tips}
                          helperText={`${values.tips && CTEXT(values.tips[tipIndex].message, 'tips')}`}
                          InputProps={
                            {
                              additionalEndAdornments: <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    console.log('Remove Tip', tipIndex);
                                    tipArrayHelpers.remove(tipIndex);
                                  }}><CloseIcon/></IconButton>
                              </InputAdornment>
                            }
                          }

                        />

                        <ContentBaseField
                          sx={formStyles.input}
                          key={`tips[${tipIndex}].severity`}
                          id={`tips[${tipIndex}].severity`}
                          name={`tips[${tipIndex}].severity`}
                          label={`Severity`}
                          value={tip.severity}
                          onChange={handleChange}
                          select
                          variant="outlined"
                          validation={validation}
                          touched={!!touched.tips}
                        >
                          {
                            Object.keys(SeverityState)
                              .map((key: string) => <MenuItem
                                  key={`tips[${tipIndex}].severity.${key}`}
                                  value={key}
                                >
                                  {headerCase(key)}
                                </MenuItem>
                              )
                          }
                        </ContentBaseField>
                        {/*<Grid item>*/}
                        {/*  <IconButton onClick={() => tipArrayHelpers.remove(index)}><CloseIcon/></IconButton>*/}
                        {/*</Grid>*/}
                      </Grid>
                    )}
                    <Button startIcon={<AddIcon/>} onClick={() => {
                      console.log('Adding tip...');
                      tipArrayHelpers.push({
                        severity: SeverityState.INFO,
                        message: ''
                      });
                    }
                    }>Add Tip</Button>
                  </Grid>
                }
              </FieldArray>
            </AccordionDetails>
          </Accordion>

          <Accordion sx={formStyles.input}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Grid container direction="column">
                <Typography variant="body1">📚 Learn More
                  ({(values.details ? values.details : '').split(' ').length - 1} words)</Typography>
                <Typography variant="caption" color="textSecondary">Used to explain the "why" behind our
                  methodology</Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <ContentField
                key={`details`}
                id={`details`}
                name={`details`}
                label={`Details`}
                defaultValue={values.details}
                onBlur={handleChange}
                sx={formStyles.input}
                variant="outlined"
                placeholder="Details (Learn More)"
                validation={validation}
                touched={!!touched.details}
                multiline
              />
            </AccordionDetails>
          </Accordion>

          <Accordion sx={formStyles.input}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Grid container direction="column">
                <Typography variant="body1">💖 Points ({numPoints(values.points)})</Typography>
                <Typography variant="caption" color="textSecondary">Wellness points to reward to the
                  user</Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid item container direction="row" wrap="nowrap">
                <Grid item xs={3}>
                  <TextField
                    key={`points.health`}
                    id={`points.health`}
                    name={`points.health`}
                    label={`Health`}
                    defaultValue={values.points && values.points.health ? values.points.health : null}
                    onBlur={handleChange}
                    sx={formStyles.input}
                    style={{ width: '100%' }}
                    variant="outlined"
                    type="number"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    key={`points.environment`}
                    id={`points.environment`}
                    name={`points.environment`}
                    label={`Environment`}
                    defaultValue={values.points && values.points.environment ? values.points.environment : null}
                    onBlur={handleChange}
                    sx={formStyles.input}
                    style={{ width: '100%' }}
                    variant="outlined"
                    type="number"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    key={`points.behavior`}
                    id={`points.behavior`}
                    name={`points.behavior`}
                    label={`Behavior`}
                    defaultValue={values.points && values.points.behavior ? values.points.behavior : null}
                    onBlur={handleChange}
                    sx={formStyles.input}
                    style={{ width: '100%' }}
                    variant="outlined"
                    type="number"
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    key={`points.cognition`}
                    id={`points.cognition`}
                    name={`points.cognition`}
                    label={`Cognition`}
                    defaultValue={values.points && values.points.cognition ? values.points.cognition : null}
                    onBlur={handleChange}
                    sx={formStyles.input}
                    style={{ width: '100%' }}
                    variant="outlined"
                    type="number"
                  />
                </Grid>
              </Grid>

            </AccordionDetails>
          </Accordion>

          <Accordion sx={formStyles.input}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Grid container direction="column">
                <Typography variant="body1">🥁 Actions ({(values.actions ? values.actions : []).length})</Typography>
                <Typography variant="caption" color="textSecondary">Coded actions for the UI to respond to if this
                  dialog
                  appears</Typography>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <DialogFormActionsField classes={formStyles} values={values} setFieldValue={setFieldValue}
                                      handleChange={handleChange}/>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={isCompletion(values.completionCriteria)} sx={formStyles.input}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCompletion(values.completionCriteria)}
                    onChange={
                      (event: ChangeEvent<HTMLInputElement>) => {
                        if (event.target.checked) {
                          setFieldValue('completionCriteria', defaultAttributeField());
                        } else {
                          setFieldValue('completionCriteria', null);
                        }
                      }
                    }
                  />
                }
                style={{ color: '#000' }}
                label="⭐ Mark As Completion Event"
              />
            </AccordionSummary>
            <AccordionDetails>
              <Grid item container direction="row" wrap="nowrap">
                <Grid item xs={4}>
                  <ContentBaseField
                    key={`completionCriteria.value`}
                    id={`completionCriteria.value`}
                    name={`completionCriteria.value`}
                    label={`Value`}
                    defaultValue={values.completionCriteria && values.completionCriteria.value ? values.completionCriteria.value : null}
                    onBlur={handleChange}
                    sx={formStyles.input}
                    style={{ width: '100%' }}
                    variant="outlined"
                    type="number"
                    helperText="Check to slap a range"
                    inputProps={{
                      step: '0.01'
                    }}
                    validation={validation}
                    touched={!!touched.completionCriteria}
                    InputProps={
                      {
                        notched: true,
                        endAdornment: <Checkbox
                          key={`completionCriteria.hasRange`}
                          id={`completionCriteria.hasRange`}
                          onChange={
                            (event: ChangeEvent<HTMLInputElement>) => {
                              if (values.completionCriteria && typeof values.completionCriteria.min === 'number') {
                                setFieldValue('values.completionCriteria.min', null);
                                setFieldValue('values.completionCriteria.max', null);
                              } else {
                                setFieldValue('values.completionCriteria.min', 0);
                                setFieldValue('values.completionCriteria.max', 0);
                              }
                            }
                          }
                        />
                      }
                    }
                  />
                </Grid>
                {values.completionCriteria && typeof values.completionCriteria.min === 'number' && typeof values.completionCriteria.max === 'number' ?
                  <React.Fragment>
                    <Grid item xs={4}>
                      <TextField
                        key={`completionCriteria.min`}
                        id={`completionCriteria.min`}
                        name={`completionCriteria.min`}
                        label={`Min`}
                        defaultValue={values.completionCriteria && values.completionCriteria.min ? values.completionCriteria.min : 0}
                        onBlur={handleChange}
                        sx={formStyles.input}
                        style={{ width: '100%' }}
                        variant="outlined"
                        type="number"
                        error={values.completionCriteria && typeof values.completionCriteria.min === 'number' && typeof values.completionCriteria.max === 'number' && values.completionCriteria.min > values.completionCriteria.max}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        key={`completionCriteria.max`}
                        id={`completionCriteria.max`}
                        name={`completionCriteria.max`}
                        label={`Max`}
                        defaultValue={values.completionCriteria && values.completionCriteria.max ? values.completionCriteria.max : 1}
                        onBlur={handleChange}
                        sx={formStyles.input}
                        style={{ width: '100%' }}
                        variant="outlined"
                        type="number"
                      />
                    </Grid>
                  </React.Fragment>
                  : null
                }
              </Grid>

            </AccordionDetails>
          </Accordion>
        </div>

        <Grid container justifyContent="center" alignItems="center" style={{ maxWidth: 550, padding: 24 }}>
          <Button variant="contained" type="submit" color="primary" style={{ width: '100%' }}>Save</Button>
        </Grid>

        {
          Object.keys(errors).length > 0 &&
          <Alert severity="error">
            <AlertTitle>Form Errors</AlertTitle>
            <ul>{Object.keys(errors).map((key: string) => <li key={key}>{(errors as any)[key]}</li>)}</ul>
          </Alert>
        }

      </Grid>
    </Form>
  );
}
